import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faInfoCircle,
    faPlay,
    faCheckCircle,
    faClock,
    faArchive,
    faBriefcase,
    faGlobe,
    faLightbulb,
    faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import PageContainer from './PageContainer';
import client from '../api/client';
import { useAuth } from '../auth/AuthContext';

const Help: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { session } = useAuth();
    const [feedbackType, setFeedbackType] = useState<'bug' | 'suggestion' | 'other'>('bug');
    const [feedbackSubject, setFeedbackSubject] = useState('');
    const [feedbackBody, setFeedbackBody] = useState('');
    const [reporterEmail, setReporterEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedbackRefCode, setFeedbackRefCode] = useState<string | null>(null);
    const [feedbackError, setFeedbackError] = useState<string | null>(null);
    const isAnonymous = !session;
    const sourcePage = useMemo(
        () => `${location.pathname}${location.search}${location.hash}`,
        [location.pathname, location.search, location.hash]
    );

    const handleFeedbackSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmedSubject = feedbackSubject.trim();
        const trimmedBody = feedbackBody.trim();
        const trimmedEmail = reporterEmail.trim();

        if (!trimmedSubject || !trimmedBody) {
            setFeedbackError('Please add a subject and description.');
            return;
        }
        if (isAnonymous && !trimmedEmail) {
            setFeedbackError('Email is required when not signed in.');
            return;
        }

        setIsSubmitting(true);
        setFeedbackError(null);
        setFeedbackRefCode(null);

        try {
            const payload: {
                type: 'bug' | 'suggestion' | 'other';
                subject: string;
                body: string;
                reporter_email?: string;
                source_page: string;
                app_release: string;
            } = {
                type: feedbackType,
                subject: trimmedSubject,
                body: trimmedBody,
                source_page: sourcePage,
                app_release: process.env.REACT_APP_VERSION || '',
            };
            if (isAnonymous) {
                payload.reporter_email = trimmedEmail;
            }

            const response = await client.post('/incidents', payload);
            const refCode = response?.data?.ref_code;
            setFeedbackRefCode(typeof refCode === 'string' ? refCode : null);
            setFeedbackSubject('');
            setFeedbackBody('');
            if (isAnonymous) setReporterEmail('');
        } catch (error) {
            console.error('Failed to submit feedback', error);
            setFeedbackError('Could not submit feedback right now. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const sections = [
        {
            title: "Today — what needs you now.",
            icon: faGlobe,
            content: (
                <div className="space-y-4">
                    <p className="text-theme-secondary">Today groups what is due and waiting. You pick what to do next.</p>

                    <div className="bg-theme-secondary p-4 rounded-xl border border-theme">
                        <h4 className="text-indigo-400 font-semibold mb-2 flex items-center gap-2">
                            <FontAwesomeIcon icon={faLightbulb} className="text-xs" />
                            At the top of Today
                        </h4>
                        <div className="text-xs text-theme-muted leading-relaxed space-y-2">
                            <p>The briefing shows what is waiting, including sync status.</p>
                            <p>• <strong>Health Check:</strong> Status of your background synchronization.</p>
                        </div>
                    </div>

                    <div className="bg-theme-secondary p-4 rounded-xl border border-theme">
                        <h4 className="text-indigo-400 font-semibold mb-2 flex items-center gap-2">
                            <FontAwesomeIcon icon={faGlobe} className="text-xs" />
                            Context Switcher (Filtering)
                        </h4>
                        <p className="text-xs text-theme-muted leading-relaxed">
                            Use the icons below the briefing to switch contexts. Selecting <strong>"Home Mode"</strong> (🧸) will show only parenthood-related items, while <strong>"Work Mode"</strong> (💼) focuses on career and business. This helps reduce mental load by focusing on one domain at a time.
                        </p>
                    </div>

                    <div className="bg-theme-secondary p-4 rounded-xl border border-theme">
                        <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-xs" />
                            Decision Hub
                        </h4>
                        <p className="text-xs text-theme-muted leading-relaxed">
                            Some cards need a decision from you (sign, confirm a date). You’ll see them marked clearly.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Work Item Cards & Actions",
            icon: faBriefcase,
            content: (
                <div className="space-y-4">
                    <p className="text-theme-secondary">Every item on your board is represented by a card with these key indicators:</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 bg-theme-secondary rounded-lg border border-theme">
                            <div className="text-xs font-bold text-theme-muted mb-1 uppercase">Visual Logic</div>
                            <div className="space-y-2">
                                <div className="text-[11px] text-theme-secondary">
                                    • <strong>Left Border:</strong> Identifies the Life Domain (e.g., Blue for Work, Green for Finance).
                                </div>
                                <div className="text-[11px] text-theme-secondary">
                                    • <strong>Full Border:</strong> Indicates Urgency (e.g., Red for Overdue, Orange for Today).
                                </div>
                                <div className="text-[11px] text-theme-secondary">
                                    • <strong>Subtle Glow:</strong> Appears on hover in the domain's color for psychological focus.
                                </div>
                            </div>
                        </div>
                        <div className="p-3 bg-theme-secondary rounded-lg border border-theme">
                            <div className="text-xs font-bold text-theme-muted mb-1 uppercase">Action Required</div>
                            <p className="text-[11px] text-theme-muted">
                                Some cards need a decision from you (sign, confirm a date). You’ll see them marked clearly.
                            </p>
                        </div>
                    </div>

                    <div className="p-4 bg-theme-secondary rounded-xl border border-theme">
                        <h4 className="text-indigo-400 font-semibold mb-3 flex items-center gap-2">
                            Quick Action Buttons
                        </h4>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="text-center p-2 rounded bg-theme-tertiary border border-theme shadow-sm">
                                <FontAwesomeIcon icon={faPlay} className="text-theme-muted mb-1" />
                                <div className="text-[10px] font-bold text-theme">START</div>
                                <div className="text-[9px] text-theme-muted">Moves item to Priority</div>
                            </div>
                            <div className="text-center p-2 rounded bg-theme-tertiary border border-theme shadow-sm">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500 mb-1" />
                                <div className="text-[10px] font-bold text-theme">DONE</div>
                                <div className="text-[9px] text-theme-muted">Marks complete & hides</div>
                            </div>
                            <div className="text-center p-2 rounded bg-theme-tertiary border border-theme shadow-sm">
                                <FontAwesomeIcon icon={faClock} className="text-amber-500 mb-1" />
                                <div className="text-[10px] font-bold text-theme">SNOOZE</div>
                                <div className="text-[9px] text-theme-muted">Hides item for 24 hours</div>
                            </div>
                            <div className="text-center p-2 rounded bg-theme-tertiary border border-theme shadow-sm">
                                <FontAwesomeIcon icon={faArchive} className="text-theme-muted mb-1" />
                                <div className="text-[10px] font-bold text-theme">ARCHIVE</div>
                                <div className="text-[9px] text-theme-muted">Moves to storage</div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Connecting Your Gmail",
            icon: faEnvelope,
            content: (
                <div className="space-y-4">
                    <p className="text-theme-secondary">Connect Gmail to pull candidates into Triage. If the link breaks, you’ll see reconnect — not a fake empty board.</p>

                    <div className="bg-theme-secondary p-4 rounded-xl border border-theme space-y-3">
                        <h4 className="text-emerald-500 font-semibold flex items-center gap-2">
                            🚀 Setup Steps
                        </h4>
                        <div className="text-xs text-theme-muted space-y-2">
                            <p>1. Open the <strong>"Connect Gmail"</strong> modal from the Today page header.</p>
                            <p>2. Click <strong>"Authorize Gmail Access"</strong> and follow the Google OAuth prompts.</p>
                            <p>3. Once connected, use the <strong>"Start Sync"</strong> button to fetch your recent history.</p>
                            <p>4. Enable <strong>"Auto-Sync"</strong> to have the system check for new items hourly in the background.</p>
                        </div>
                    </div>

                    <div className="bg-red-500/5 p-4 rounded-xl border border-red-500/20">
                        <h4 className="text-sm font-bold text-theme mb-3">Power User Tips</h4>
                        <div className="text-xs text-theme-muted leading-relaxed space-y-2">
                            <p>• <strong>"0 Items Found":</strong> Ensure your date range isn't too narrow. Try a custom query if you're looking for something specific.</p>
                            <p>• <strong>"Expecting value":</strong> If you see this error in the email viewer, your session may have expired. Click <strong>"Disconnect"</strong> and then <strong>"Connect"</strong> again to refresh your security keys.</p>
                            <p>• <strong>Sync Stuck at "IN PROGRESS":</strong> This can happen if the server is restarted during a sync. A simple refresh or server restart will clear the lock.</p>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "The item drawer — decide in seconds",
            icon: faLightbulb,
            content: (
                <div className="space-y-4">
                    <p className="text-theme-secondary">
                        Click any item on Today or Workbench. The drawer is not a form first — it is a
                        ten-second decision. We already read the email or document. You keep the call.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="p-3 bg-theme-secondary rounded-lg border border-theme">
                            <div className="text-xs font-bold text-theme mb-1">Overview</div>
                            <p className="text-[11px] text-theme-muted">
                                What this is, and what to do now. One next step, then Start / Snooze / Dismiss.
                            </p>
                        </div>
                        <div className="p-3 bg-theme-secondary rounded-lg border border-theme">
                            <div className="text-xs font-bold text-theme mb-1">Edit</div>
                            <p className="text-[11px] text-theme-muted">
                                Title, notes, domain, and dates when you need to edit the record.
                            </p>
                        </div>
                        <div className="p-3 bg-theme-secondary rounded-lg border border-theme">
                            <div className="text-xs font-bold text-theme mb-1">Evidence</div>
                            <p className="text-[11px] text-theme-muted">
                                Why we think this — invoices, notices, scam checks, full decision paths. Only appears when there is something to show.
                            </p>
                        </div>
                    </div>

                    <div className="p-4 bg-indigo-500/5 rounded-xl border border-indigo-500/20 space-y-2">
                        <h4 className="text-indigo-500 font-semibold">Three moves. Learn once.</h4>
                        <p className="text-xs text-theme-muted"><strong>Start</strong> — this is yours now. It leaves triage and becomes active work.</p>
                        <p className="text-xs text-theme-muted"><strong>Snooze</strong> — bring it back later (1 day, 3 days, or a week). Undo is always on the toast.</p>
                        <p className="text-xs text-theme-muted"><strong>Dismiss</strong> — not work. Let it go. Undo is on the toast.</p>
                    </div>

                    <div className="space-y-2">
                        <h4 className="text-sm font-bold text-theme">When you would open it</h4>
                        <div className="text-xs text-theme-muted space-y-2">
                            <p><strong>Interview invite.</strong> A supported request sits on Overview. Open in Gmail from the card. Mark it an Application if that is what it is.</p>
                            <p><strong>Invoice or bill.</strong> Amount and due date sit on Overview when we have them — labeled as due, estimate, or contract total, not as a Pay command.</p>
                            <p><strong>School / agency notice.</strong> Evidence shows facts, missing info, and paths. We do not pick a path. You do. Copy a response if you want the wording.</p>
                            <p><strong>Something that feels off.</strong> A high scam risk shows a safe first step and no &quot;reply&quot; button. Open Evidence for the full check.</p>
                            <p><strong>Noise from a sender.</strong> &quot;Not a task — ignore source&quot; teaches us to stop creating items from that sender.</p>
                            <p><strong>Prep for a real event.</strong> In Edit → Scheduling, add the event date. Accept &quot;start N days before&quot; so it appears on Today when you actually need to prepare.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 bg-theme-secondary rounded-lg border border-theme">
                            <div className="text-xs font-bold text-theme-muted mb-1 uppercase">Notes vs. Summary</div>
                            <p className="text-[11px] text-theme-muted">
                                The <strong>Summary</strong> is extracted for a glance. <strong>Notes</strong> are yours — follow-ups, draft replies, reminders.
                            </p>
                        </div>
                        <div className="p-3 bg-theme-secondary rounded-lg border border-theme">
                            <div className="text-xs font-bold text-theme-muted mb-1 uppercase">Undo everywhere</div>
                            <p className="text-[11px] text-theme-muted">
                                Start, snooze, dismiss, classify, and complete all toast with Undo. You can move fast without being afraid.
                            </p>
                        </div>
                    </div>
                    <div className="mt-8 pt-8 border-t border-theme">
                        <p className="text-sm text-theme-muted mb-4">Need the full list?</p>
                        <button
                            onClick={() => navigate('/app/workbench')}
                            className="px-6 py-2 bg-theme-secondary text-theme rounded-xl border border-theme hover:bg-theme-card-hover transition-all text-xs font-bold uppercase tracking-widest"
                        >
                            Enter Workbench
                        </button>
                    </div>
                </div>
            )
        }
    ];

    return (
        <PageContainer>
            <div className="max-w-[1000px] mx-auto">
                <header className="mb-12">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-14 w-14 rounded-2xl bg-electric-blue/20 flex items-center justify-center shadow-xl shadow-electric-blue/10">
                            <span className="text-electric-blue font-black text-2xl">?</span>
                        </div>
                        <h1 className="text-4xl font-bold text-theme tracking-tight">Manual & Intelligence</h1>
                    </div>
                    <p className="text-theme-muted text-xs font-bold uppercase tracking-[0.3em] ml-[52px]">Today — what needs you now.</p>
                </header>

                <div className="space-y-6">
                    {sections.map((section, idx) => (
                        <Accordion
                            key={idx}
                            disableGutters
                            elevation={0}
                            sx={{
                                backgroundColor: 'var(--color-bg-card)',
                                backgroundImage: 'none',
                                color: 'var(--color-text-primary)',
                                border: '1px solid var(--color-border)',
                                borderRadius: '24px !important',
                                overflow: 'hidden',
                                mb: 2,
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': {
                                    backgroundColor: 'var(--color-bg-card)',
                                    borderColor: 'var(--color-border)',
                                    boxShadow: 'var(--shadow-card)'
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<FontAwesomeIcon icon={faChevronDown} className="text-theme-muted" />}
                                sx={{
                                    px: 4,
                                    py: 1,
                                    backgroundColor: 'transparent',
                                    '& .MuiAccordionSummary-content': {
                                        margin: '12px 0'
                                    }
                                }}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="h-8 w-8 rounded-xl bg-theme-secondary border border-theme flex items-center justify-center">
                                        <FontAwesomeIcon icon={section.icon} className="text-indigo-500 text-sm" />
                                    </div>
                                    <Typography className="font-semibold text-lg">{section.title}</Typography>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 4, pb: 4, pt: 0, backgroundColor: 'transparent' }}>
                                <div className="text-theme-secondary">
                                    {section.content}
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </div>

                <section className="mt-12 rounded-2xl border border-theme bg-theme-secondary p-6">
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold text-theme">Send feedback</h2>
                        <p className="text-xs text-theme-muted mt-1">
                            Report an issue or suggest an improvement. We will track it with a reference code.
                        </p>
                    </div>

                    <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-theme-muted mb-1">
                                Type
                            </label>
                            <select
                                value={feedbackType}
                                onChange={(e) => setFeedbackType(e.target.value as 'bug' | 'suggestion' | 'other')}
                                className="w-full rounded-lg border border-theme bg-theme-tertiary px-3 py-2 text-sm text-theme focus:outline-none"
                            >
                                <option value="bug">Bug</option>
                                <option value="suggestion">Suggestion</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-theme-muted mb-1">
                                Subject
                            </label>
                            <input
                                type="text"
                                value={feedbackSubject}
                                onChange={(e) => setFeedbackSubject(e.target.value)}
                                className="w-full rounded-lg border border-theme bg-theme-tertiary px-3 py-2 text-sm text-theme focus:outline-none"
                                placeholder="Short summary"
                                maxLength={200}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-theme-muted mb-1">
                                Description
                            </label>
                            <textarea
                                value={feedbackBody}
                                onChange={(e) => setFeedbackBody(e.target.value)}
                                className="min-h-[120px] w-full rounded-lg border border-theme bg-theme-tertiary px-3 py-2 text-sm text-theme focus:outline-none"
                                placeholder="What happened, what you expected, and any context."
                                maxLength={10000}
                                required
                            />
                        </div>

                        {isAnonymous && (
                            <div>
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-theme-muted mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={reporterEmail}
                                    onChange={(e) => setReporterEmail(e.target.value)}
                                    className="w-full rounded-lg border border-theme bg-theme-tertiary px-3 py-2 text-sm text-theme focus:outline-none"
                                    placeholder="you@example.com"
                                    maxLength={320}
                                    required
                                />
                            </div>
                        )}

                        {feedbackError && (
                            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                                {feedbackError}
                            </div>
                        )}

                        {feedbackRefCode && (
                            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
                                Thanks. Your reference is <strong>{feedbackRefCode}</strong>.
                            </div>
                        )}

                        <div className="flex items-center justify-between gap-4">
                            <p className="text-[11px] text-theme-muted">Source page: {sourcePage}</p>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="rounded-lg border border-theme bg-theme-tertiary px-4 py-2 text-xs font-bold uppercase tracking-wider text-theme transition hover:bg-theme-card-hover disabled:opacity-60"
                            >
                                {isSubmitting ? 'Sending...' : 'Send feedback'}
                            </button>
                        </div>
                    </form>
                </section>

                <footer className="mt-16 text-center pb-20">
                    <p className="text-[10px] text-theme-muted font-bold uppercase tracking-widest mb-4">Version 2.0</p>
                    <div className="inline-flex items-center gap-6 p-4 rounded-2xl bg-theme-secondary border border-theme shadow-sm">
                        <div className="text-left">
                            <div className="text-[10px] text-theme-muted mb-1">PRO TIP</div>
                            <div className="text-xs text-theme-secondary">Press <kbd className="bg-theme-tertiary px-1.5 py-0.5 rounded border border-theme font-mono text-[10px] text-theme">J</kbd> or <kbd className="bg-theme-tertiary px-1.5 py-0.5 rounded border border-theme font-mono text-[10px] text-theme">K</kbd> to navigate items with your keyboard.</div>
                        </div>
                    </div>
                </footer>
            </div>
        </PageContainer>
    );
};

export default Help;
